/**
 * Custom Hook for Container Form (Create & Edit)
 * 
 * Manages all business logic for container form including:
 * - State management
 * - Data loading (edit mode)
 * - Form validation
 * - Submit logic (create with progress modal, edit simple)
 * - File upload (create only)
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import type { DateValue } from 'react-aria-components';
import { parseDate } from '@internationalized/date';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast-service';
import { storage } from '@/lib/local-storage';
import { apiGet, ApiClientError } from '@/lib/api-client';
import type { FolderResponse } from '@/types/api/folders';
import type { UploadFileItem } from '@/components/application/documents/file-upload-preview-modal';
import type { CreationProgress } from '@/components/application/containers/modals/container-creation-progress-modal';

// API Hooks
import { useMe } from '@/hooks/api/use-auth';
import { usePorts, usePortById } from '@/hooks/api/use-ports';
import { useIncoterms } from '@/hooks/api/use-incoterms';
import { useShipmentStatuses, useCargoTypes, useTransportModes, useContainerTypes } from '@/hooks/api/use-enums';
import { useHSCodes } from '@/hooks/api/use-hs-codes';
import { useReceivers } from '@/hooks/api/use-receivers';
import { useCreateShipment, useUpdateShipment, type ShipmentCreateData, type ShipmentUpdateData } from '@/hooks/api/use-shipments';
import { useShipmentById } from '@/hooks/api/use-containers-recap';
import { useCreateCompany } from '@/hooks/api/use-companies';
import { useCarriers } from '@/hooks/api/use-carriers';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useOrders } from '@/hooks/api/use-orders';
import { CompanyType } from '@/types/api/admin';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';

// ============================================================================
// TYPES
// ============================================================================

export interface ContainerFormData {
    // Booking Info (new primary identifier)
    bookingNumber: string;  // Required - carrier booking reference
    blNumber: string;       // Optional - Bill of Lading number (needed for tracking)
    containerNumbers: string[]; // Optional - ISO container numbers (assigned by carrier later)
    status: string;         // ShipmentStatus (DRAFT, BOOKED, IN_TRANSIT, etc.)

    // Route
    originPortId: string;
    originPortDisplay: string;
    destinationPortId: string;
    destinationPortDisplay: string;
    eta: string;
    carrierName: string;
    carrierCompanyId: string;

    // Order
    orderIdMode: 'select' | 'manual';
    purchaseOrderNumber: string;

    // Goods
    hsCodePrimary: string;
    hsCodeDisplay: string;
    quantityValue: string;
    quantityUnit: 'kg' | 'cbm' | 'packages';
    valueAmount: string;
    valueCurrency: string;

    // Packing List
    cargoDescription: string;

    // Additional Info
    transportMode: string;
    cargoType: string;
    containerType: string;
    temperatureSetpoint: string;
    oxygenPercentage: string;
    co2Percentage: string;

    // Receiver
    consigneeCompanyId: string;

    // Incoterms
    incoterms: string;

    // Vessel Info
    vesselName: string;
    voyageNumber: string;

    // Transshipment Ports
    transshipmentPorts: Array<{ portId: string; portDisplay: string }>;
}

export interface SelectOption {
    id: string;
    label: string;
    description?: string;
    fullDescription?: string;
}

export interface UseContainerFormOptions {
    containerId?: string; // undefined = create mode, defined = edit mode
}

export interface UseContainerFormReturn {
    // State
    formData: ContainerFormData;
    isEditMode: boolean;
    isLoading: boolean;
    error: string;
    etaDate: DateValue | null;
    setEtaDate: (date: DateValue | null) => void;

    // Search States
    originPortSearch: string;
    setOriginPortSearch: (value: string) => void;
    destPortSearch: string;
    setDestPortSearch: (value: string) => void;
    hsCodeSearch: string;
    setHsCodeSearch: (value: string) => void;
    carrierSearch: string;
    setCarrierSearch: (value: string) => void;

    // Handlers
    handleInputChange: (field: keyof ContainerFormData, value: string) => void;
    handlePortSelect: (type: 'origin' | 'destination', portId: string) => void;
    handleHsCodeSelect: (code: string) => void;
    handleSubmit: (e: React.FormEvent) => Promise<void>;
    handleAddContainerNumber: () => void;
    handleRemoveContainerNumber: (index: number) => void;
    handleContainerNumberChange: (index: number, value: string) => void;

    // Transshipment ports
    transshipmentPortSearches: string[];
    setTransshipmentPortSearch: (index: number, value: string) => void;
    transshipmentPortOptions: SelectOption[][];
    handleAddTransshipmentPort: () => void;
    handleRemoveTransshipmentPort: (index: number) => void;
    handleTransshipmentPortSelect: (index: number, portId: string) => void;

    // File upload (solo create)
    selectedFiles: UploadFileItem[];
    setSelectedFiles: React.Dispatch<React.SetStateAction<UploadFileItem[]>>;
    handleFileDrop: (files: FileList) => void;
    handleRemoveFile: (fileId: string) => void;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    isUploading: boolean;

    // Modals
    showAddCarrierModal: boolean;
    setShowAddCarrierModal: (show: boolean) => void;
    showInviteReceiverModal: boolean;
    setShowInviteReceiverModal: (show: boolean) => void;
    newCarrierName: string;
    setNewCarrierName: (value: string) => void;
    handleAddReceiver: () => Promise<void>;
    handleAddCarrier: () => Promise<void>;

    // Progress (solo create)
    showCreationProgress: boolean;
    setShowCreationProgress: (show: boolean) => void;
    creationProgress: CreationProgress;
    setCreationProgress: (progress: CreationProgress) => void;

    // Options/Data
    statusOptions: SelectOption[];
    cargoTypeOptions: SelectOption[];
    transportModeOptions: SelectOption[];
    containerTypeOptions: SelectOption[];
    incotermsOptions: SelectOption[];
    receiverOptions: SelectOption[];
    carrierOptions: SelectOption[];
    originPortOptions: SelectOption[];
    destPortOptions: SelectOption[];
    hsCodeOptions: SelectOption[];
    orderOptions: SelectOption[];
    currencyOptions: SelectOption[];
    quantityUnitOptions: SelectOption[];

    // Derived
    showTemperatureFields: boolean;
    isOrdersEnabled: boolean;
    companyId: string | undefined;
}

// ============================================================================
// HOOK
// ============================================================================

export function useContainerForm(options: UseContainerFormOptions = {}): UseContainerFormReturn {
    const { containerId } = options;
    const { t } = useTranslation(['pages:containers', 'common', 'types']);
    const location = useLocation();
    const navigate = useNavigate();
    const isEditMode = !!containerId;

    // Auth & User Info
    const { data: user } = useMe();
    const companyId = user?.company_id;

    // Document types (for resolving document_type_id during upload)
    const { data: documentTypesData } = useDocumentTypes();

    // Menu Configuration
    const { isMenuItemEnabled } = useMenuConfiguration();
    const isOrdersEnabled = isMenuItemEnabled('orders');

    // Form State
    const [formData, setFormData] = useState<ContainerFormData>({
        bookingNumber: '',
        blNumber: '',
        containerNumbers: [''],
        status: 'DRAFT',
        originPortId: '',
        originPortDisplay: '',
        destinationPortId: '',
        destinationPortDisplay: '',
        eta: '',
        carrierName: '',
        carrierCompanyId: '',
        orderIdMode: 'manual',
        purchaseOrderNumber: '',
        hsCodePrimary: '',
        hsCodeDisplay: '',
        quantityValue: '',
        quantityUnit: 'kg',
        valueAmount: '',
        valueCurrency: 'USD',
        cargoDescription: '',
        transportMode: 'sea',
        cargoType: 'FCL',
        containerType: 'dry',
        temperatureSetpoint: '',
        oxygenPercentage: '',
        co2Percentage: '',
        consigneeCompanyId: '',
        incoterms: '',
        vesselName: '',
        voyageNumber: '',
        transshipmentPorts: [],
    });

    // Track if form has been populated from shipment data (edit mode)
    const [formPopulated, setFormPopulated] = useState(false);

    // Search States
    const [originPortSearch, setOriginPortSearch] = useState('');
    const [destPortSearch, setDestPortSearch] = useState('');
    const [hsCodeSearch, setHsCodeSearch] = useState('');
    const [carrierSearch, setCarrierSearch] = useState('');

    // Debounced search states for ports (500ms delay)
    const [debouncedOriginPortSearch, setDebouncedOriginPortSearch] = useState('');
    const [debouncedDestPortSearch, setDebouncedDestPortSearch] = useState('');

    // Transshipment port search — single active search query, shared across entries
    const [transshipmentPortSearches, setTransshipmentPortSearches] = useState<string[]>([]);
    const [activeTransshipmentSearch, setActiveTransshipmentSearch] = useState('');
    const [debouncedTransshipmentSearch, setDebouncedTransshipmentSearch] = useState('');

    // Modal States
    const [showAddCarrierModal, setShowAddCarrierModal] = useState(false);
    const [showInviteReceiverModal, setShowInviteReceiverModal] = useState(false);
    const [showCreationProgress, setShowCreationProgress] = useState(false);
    const [creationProgress, setCreationProgress] = useState<CreationProgress>({ step: 'saving' });
    const [newCarrierName, setNewCarrierName] = useState('');
    const [error, setError] = useState('');

    // File Upload State (solo create)
    const [selectedFiles, setSelectedFiles] = useState<UploadFileItem[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ETA Date State
    const [etaDate, setEtaDate] = useState<DateValue | null>(null);

    // API Queries
    const { data: statusesData } = useShipmentStatuses();
    const { data: cargoTypesData } = useCargoTypes();
    const { data: transportModesData } = useTransportModes();
    const { data: containerTypesData } = useContainerTypes();
    const { data: incotermsData } = useIncoterms();
    const { data: receiversData, refetch: refetchReceivers } = useReceivers(companyId || undefined);

    // Debounce effect for origin port search (500ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedOriginPortSearch(originPortSearch);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [originPortSearch]);

    // Debounce effect for destination port search (500ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDestPortSearch(destPortSearch);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [destPortSearch]);

    // Debounce effect for transshipment port search (500ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedTransshipmentSearch(activeTransshipmentSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [activeTransshipmentSearch]);

    // Port Search (using debounced values - React Query will automatically abort previous requests)
    const { data: originPortsData } = usePorts(debouncedOriginPortSearch, undefined, debouncedOriginPortSearch.length >= 2);
    const { data: destPortsData } = usePorts(debouncedDestPortSearch, undefined, debouncedDestPortSearch.length >= 2);
    const { data: transshipmentPortsData } = usePorts(debouncedTransshipmentSearch, undefined, debouncedTransshipmentSearch.length >= 2);

    // HS Code Search
    const { data: hsCodesData } = useHSCodes(hsCodeSearch, undefined, hsCodeSearch.length >= 2);

    // Orders
    const { data: ordersData } = useOrders();

    // Carriers - Load all carriers from carriers table
    const { data: carriersData } = useCarriers({ is_active: true });

    // Edit Mode: Load existing shipment data
    const { data: shipmentData, isLoading: isLoadingShipment, error: _shipmentError } = useShipmentById(
        containerId || undefined,
        isEditMode
    );

    // Edit Mode: Fetch port details for pre-populating display names
    const { data: originPortData } = usePortById(
        shipmentData?.origin_port_id || undefined,
        isEditMode && !!shipmentData?.origin_port_id
    );
    const { data: destPortData } = usePortById(
        shipmentData?.destination_port_id || undefined,
        isEditMode && !!shipmentData?.destination_port_id
    );

    // Mutations
    const createShipment = useCreateShipment();
    const updateShipment = useUpdateShipment(containerId || '');
    const createCompany = useCreateCompany();

    // Carrier options - will be loaded from API, plus manual entries
    const [customCarriers, setCustomCarriers] = useState<Array<{ id: string; label: string }>>([]);

    // Populate form when shipment data loads (edit mode)
    useEffect(() => {
        if (isEditMode && shipmentData && !formPopulated) {
            // Determine quantity unit and value based on what's populated
            let quantityValue = '';
            let quantityUnit: 'kg' | 'cbm' | 'packages' = 'kg';

            if (shipmentData.total_weight_kg) {
                quantityValue = shipmentData.total_weight_kg.toString();
                quantityUnit = 'kg';
            } else if (shipmentData.total_volume_cbm) {
                quantityValue = shipmentData.total_volume_cbm.toString();
                quantityUnit = 'cbm';
            } else if (shipmentData.total_packages) {
                quantityValue = shipmentData.total_packages.toString();
                quantityUnit = 'packages';
            }

            // Parse controlled atmosphere from extra_metadata
            let oxygenPercentage = '';
            let co2Percentage = '';
            if (shipmentData.extra_metadata?.controlled_atmosphere) {
                const ca = shipmentData.extra_metadata.controlled_atmosphere as {
                    oxygen_percentage?: string;
                    co2_percentage?: string;
                };
                oxygenPercentage = ca.oxygen_percentage || '';
                co2Percentage = ca.co2_percentage || '';
            }

            setFormData({
                bookingNumber: shipmentData.booking_number || '',
                blNumber: shipmentData.bl_number || '',
                containerNumbers: [shipmentData.container_number || ''],
                status: shipmentData.status?.toUpperCase() || 'DRAFT',
                originPortId: shipmentData.origin_port_id || '',
                originPortDisplay: '', // Will be set when port data loads
                destinationPortId: shipmentData.destination_port_id || '',
                destinationPortDisplay: '', // Will be set when port data loads
                eta: shipmentData.estimated_arrival_date || '',
                carrierName: shipmentData.carrier_name || '',
                carrierCompanyId: '', // Carrier company ID not stored directly
                orderIdMode: 'manual',
                purchaseOrderNumber: shipmentData.purchase_order_number || '',
                hsCodePrimary: shipmentData.hs_code_primary || '',
                hsCodeDisplay: shipmentData.hs_code_primary || '',
                quantityValue,
                quantityUnit,
                valueAmount: shipmentData.total_value_usd?.toString() || '',
                valueCurrency: shipmentData.currency || 'USD',
                cargoDescription: shipmentData.cargo_description || '',
                transportMode: shipmentData.transport_mode?.toLowerCase() || 'sea',
                cargoType: shipmentData.cargo_type?.toUpperCase() || 'FCL',
                containerType: shipmentData.container_type?.toLowerCase() || 'dry',
                temperatureSetpoint: shipmentData.temperature_setpoint_celsius?.toString() || '',
                oxygenPercentage,
                co2Percentage,
                consigneeCompanyId: shipmentData.consignee_company_id || '',
                incoterms: shipmentData.incoterms || '',
                vesselName: shipmentData.vessel_name || '',
                voyageNumber: shipmentData.voyage_number || '',
                transshipmentPorts: Array.isArray(shipmentData.extra_metadata?.transshipment_ports)
                    ? (shipmentData.extra_metadata.transshipment_ports as Array<{ port_id: string; port_name: string }>)
                        .map(tp => ({ portId: tp.port_id, portDisplay: tp.port_name }))
                    : [],
            });

            // Parse and set ETA date
            if (shipmentData.estimated_arrival_date) {
                try {
                    const date = parseDate(shipmentData.estimated_arrival_date.split('T')[0]);
                    setEtaDate(date);
                } catch {
                    // Invalid date format, leave as null
                }
            }

            setFormPopulated(true);
        }
    }, [isEditMode, shipmentData, formPopulated]);

    // When carriers data loads and we have a carrierName but no carrierCompanyId in edit mode,
    // try to match the carrier name to find the SCAC code
    useEffect(() => {
        if (isEditMode && formData.carrierName && !formData.carrierCompanyId && carriersData?.carriers) {
            // Try to find carrier by exact name match
            const matchedCarrier = carriersData.carriers.find(
                c => c.name.toLowerCase() === formData.carrierName.toLowerCase()
            );
            
            if (matchedCarrier) {
                setFormData(prev => ({
                    ...prev,
                    carrierCompanyId: matchedCarrier.scac
                }));
            }
        }
    }, [isEditMode, formData.carrierName, formData.carrierCompanyId, carriersData]);

    // Update port display names when port data loads (edit mode)
    useEffect(() => {
        if (isEditMode && originPortData?.display_name) {
            setFormData(prev => ({ ...prev, originPortDisplay: originPortData.display_name }));
        }
    }, [isEditMode, originPortData]);

    useEffect(() => {
        if (isEditMode && destPortData?.display_name) {
            setFormData(prev => ({ ...prev, destinationPortDisplay: destPortData.display_name }));
        }
    }, [isEditMode, destPortData]);

    // Derived Data
    const statusOptions = useMemo(() => {
        return statusesData?.values.map(v => ({ id: v.value, label: v.label })) || [
            { id: 'DRAFT', label: 'Draft' }
        ];
    }, [statusesData]);

    const cargoTypeOptions = useMemo(() => {
        return cargoTypesData?.values.map(v => ({ id: v.value, label: v.label })) || [];
    }, [cargoTypesData]);

    const transportModeOptions = useMemo(() => {
        return transportModesData?.values.map(v => ({ id: v.value, label: v.label })) || [];
    }, [transportModesData]);

    const containerTypeOptions = useMemo(() => {
        return containerTypesData?.values.map(v => ({ id: v.value, label: v.label, description: v.description || undefined })) || [];
    }, [containerTypesData]);

    const incotermsOptions = useMemo(() => {
        return incotermsData?.incoterms.map(inc => ({
            id: inc.code,
            label: `${inc.code} - ${inc.name}`,
            description: inc.risk_transfer_point,
            fullDescription: inc.description ?? undefined,
        })) || [];
    }, [incotermsData]);

    const receiverOptions = useMemo(() => {
        const options = [
            { id: '', label: t('form.receiverNotDefined', 'Receiver not defined yet') }
        ];
        if (receiversData?.receivers) {
            options.push(...receiversData.receivers.map(r => ({
                id: r.company_id,
                label: `${r.company_name}${r.country ? ` (${r.country})` : ''}`
            })));
        }
        return options;
    }, [receiversData, t]);

    const carrierOptions = useMemo(() => {
        const options = [
            { id: '', label: t('form.carrierNotDefined', 'Carrier not defined yet') }
        ];
        
        // Add carriers from API (from carriers table)
        if (carriersData?.carriers) {
            options.push(...carriersData.carriers.map(carrier => ({
                id: carrier.scac,
                label: carrier.name
            })));
        }
        
        // Add custom carriers (created during form session)
        options.push(...customCarriers);
        
        return options;
    }, [t, carriersData, customCarriers]);

    const originPortOptions = useMemo(() => {
        return originPortsData?.ports.map(p => ({
            id: p.port_id,
            label: p.display_name,
        })) || [];
    }, [originPortsData]);

    const destPortOptions = useMemo(() => {
        return destPortsData?.ports.map(p => ({
            id: p.port_id,
            label: p.display_name,
        })) || [];
    }, [destPortsData]);

    const transshipmentPortOptions = useMemo(() => {
        const options = transshipmentPortsData?.ports.map(p => ({
            id: p.port_id,
            label: p.display_name,
        })) || [];
        // Return the same options for all search slots (single shared query)
        return transshipmentPortSearches.map(() => options);
    }, [transshipmentPortsData, transshipmentPortSearches.length]);

    const hsCodeOptions = useMemo(() => {
        return hsCodesData?.map(h => ({
            id: h.code,
            label: `${h.code} - ${h.description_en}`,
        })) || [];
    }, [hsCodesData]);

    const orderOptions = useMemo(() => {
        const options = [
            { id: '', label: t('form.selectOrder', 'Select an order...') }
        ];
        if (ordersData?.orders) {
            options.push(...ordersData.orders.map(o => ({
                id: o.order_code || o.order_id,
                label: `${o.order_code}${o.target_week ? ` - Week ${o.target_week}` : ''}`
            })));
        }
        return options;
    }, [ordersData, t]);

    const currencyOptions = [
        { id: 'USD', label: 'USD' },
        { id: 'EUR', label: 'EUR' },
        { id: 'GBP', label: 'GBP' },
        { id: 'CHF', label: 'CHF' },
    ];

    const quantityUnitOptions = [
        { id: 'kg', label: 'kg (Weight)' },
        { id: 'cbm', label: 'cbm (Volume)' },
        { id: 'packages', label: 'Packages (Count)' },
    ];

    // Show temperature fields when container type is reefer
    const showTemperatureFields = formData.containerType === 'reefer';

    // Handlers
    const handleInputChange = (field: keyof ContainerFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handlePortSelect = (type: 'origin' | 'destination', portId: string) => {
        const ports = type === 'origin' ? originPortsData?.ports : destPortsData?.ports;
        const port = ports?.find(p => p.port_id === portId);

        if (type === 'origin') {
            setFormData(prev => ({
                ...prev,
                originPortId: portId,
                originPortDisplay: port?.display_name || '',
            }));
            setOriginPortSearch('');
        } else {
            setFormData(prev => ({
                ...prev,
                destinationPortId: portId,
                destinationPortDisplay: port?.display_name || '',
            }));
            setDestPortSearch('');
        }
    };

    const handleHsCodeSelect = (code: string) => {
        const hsCode = hsCodesData?.find(h => h.code === code);
        setFormData(prev => ({
            ...prev,
            hsCodePrimary: code,
            hsCodeDisplay: hsCode ? `${hsCode.code} - ${hsCode.description_en}` : code,
        }));
        setHsCodeSearch('');
    };

    // Container number list handlers
    const handleAddContainerNumber = () => {
        setFormData(prev => ({ ...prev, containerNumbers: [...prev.containerNumbers, ''] }));
    };

    const handleRemoveContainerNumber = (index: number) => {
        setFormData(prev => ({
            ...prev,
            containerNumbers: prev.containerNumbers.length > 1
                ? prev.containerNumbers.filter((_, i) => i !== index)
                : [''], // Keep at least one empty entry
        }));
    };

    const handleContainerNumberChange = (index: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            containerNumbers: prev.containerNumbers.map((cn, i) => i === index ? value : cn),
        }));
    };

    // Transshipment port handlers
    const setTransshipmentPortSearch = (index: number, value: string) => {
        setTransshipmentPortSearches(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
        setActiveTransshipmentSearch(value);
    };

    const handleAddTransshipmentPort = () => {
        setFormData(prev => ({
            ...prev,
            transshipmentPorts: [...prev.transshipmentPorts, { portId: '', portDisplay: '' }],
        }));
        setTransshipmentPortSearches(prev => [...prev, '']);
    };

    const handleRemoveTransshipmentPort = (index: number) => {
        setFormData(prev => ({
            ...prev,
            transshipmentPorts: prev.transshipmentPorts.filter((_, i) => i !== index),
        }));
        setTransshipmentPortSearches(prev => prev.filter((_, i) => i !== index));
    };

    const handleTransshipmentPortSelect = (index: number, portId: string) => {
        const port = transshipmentPortsData?.ports.find(p => p.port_id === portId);
        setFormData(prev => ({
            ...prev,
            transshipmentPorts: prev.transshipmentPorts.map((tp, i) =>
                i === index ? { portId, portDisplay: port?.display_name || '' } : tp
            ),
        }));
        setTransshipmentPortSearches(prev => {
            const next = [...prev];
            next[index] = '';
            return next;
        });
        setActiveTransshipmentSearch('');
    };

    // Add new carrier (shipping company)
    const handleAddCarrier = async () => {
        if (!newCarrierName.trim()) return;

        try {
            const newCompany = await createCompany.mutateAsync({
                company_name: newCarrierName.trim(),
                company_type: CompanyType.FREIGHT_FORWARDER,
                is_active: true,
            });

            // Add to custom carriers list
            setCustomCarriers(prev => [...prev, {
                id: newCompany.company_id,
                label: newCompany.company_name,
            }]);

            // Select the new carrier
            setFormData(prev => ({
                ...prev,
                carrierCompanyId: newCompany.company_id,
                carrierName: newCompany.company_name,
            }));

            toast.success(t('form.carrierAddedSuccess', 'Shipping company added successfully'));
            setShowAddCarrierModal(false);
            setNewCarrierName('');
        } catch (err) {
            toast.error(t('form.carrierAddedError', 'Failed to add shipping company'));
        }
    };

    // Add new receiver — redirects to business partner form
    const handleAddReceiver = async () => {
        // Save current form data to sessionStorage so we can restore it on return
        try {
            sessionStorage.setItem('container-form-draft', JSON.stringify(formData));
            if (etaDate) {
                sessionStorage.setItem('container-form-draft-eta', etaDate.toString());
            }
        } catch {
            // sessionStorage may be unavailable
        }
        navigate('/shipper/business-partners/new?type=receiver&returnTo=container-form', {
            state: navigationStateFrom(location),
        });
    };

    // On mount: check if we're returning from business partner form
    useEffect(() => {
        const state = location.state as { newReceiverId?: string } | null;
        if (state?.newReceiverId) {
            // Auto-select the new receiver
            setFormData(prev => ({
                ...prev,
                consigneeCompanyId: state.newReceiverId!,
            }));

            // Restore form data from sessionStorage
            try {
                const draft = sessionStorage.getItem('container-form-draft');
                if (draft) {
                    const parsed = JSON.parse(draft) as ContainerFormData;
                    // Keep the new receiver, restore everything else
                    setFormData(prev => ({
                        ...parsed,
                        consigneeCompanyId: state.newReceiverId || prev.consigneeCompanyId,
                    }));
                    sessionStorage.removeItem('container-form-draft');
                }
                const etaDraft = sessionStorage.getItem('container-form-draft-eta');
                if (etaDraft) {
                    try {
                        setEtaDate(parseDate(etaDraft));
                    } catch {
                        // Invalid date
                    }
                    sessionStorage.removeItem('container-form-draft-eta');
                }
            } catch {
                // sessionStorage may be unavailable
            }

            // Refetch receivers to include the new one
            refetchReceivers();

            // Clear the state to avoid re-triggering
            window.history.replaceState({}, document.title);
        } else if (!isEditMode) {
            // Browser Back from business partner form: restore draft if exists
            try {
                const draft = sessionStorage.getItem('container-form-draft');
                if (draft) {
                    const parsed = JSON.parse(draft) as ContainerFormData;
                    setFormData(parsed);
                    sessionStorage.removeItem('container-form-draft');

                    const etaDraft = sessionStorage.getItem('container-form-draft-eta');
                    if (etaDraft) {
                        try {
                            setEtaDate(parseDate(etaDraft));
                        } catch {
                            // Invalid date
                        }
                        sessionStorage.removeItem('container-form-draft-eta');
                    }
                }
            } catch {
                // sessionStorage may be unavailable
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // File upload handlers (solo create)
    const handleFileDrop = (files: FileList) => {
        const newFiles: UploadFileItem[] = Array.from(files).map(file => ({
            file,
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            customName: file.name,
            progress: 0,
            status: 'pending' as const,
        }));
        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const handleRemoveFile = (fileId: string) => {
        setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // Upload file with progress tracking to GCS
    // Parse error response from backend into a readable message
    const parseErrorResponse = (responseText: string): string => {
        try {
            const json = JSON.parse(responseText);
            if (typeof json.detail === 'string') return json.detail;
            if (Array.isArray(json.detail)) {
                return json.detail.map((e: { msg?: string; loc?: string[] }) => {
                    const field = e.loc?.slice(-1)[0] || '';
                    return field ? `${field}: ${e.msg}` : (e.msg || 'Validation error');
                }).join('; ');
            }
            return 'Upload failed';
        } catch {
            return responseText || 'Upload failed';
        }
    };

    const uploadFileWithProgress = async (fileItem: UploadFileItem, containerId: string): Promise<string> => {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        formData.append('container_id', containerId);

        if (fileItem.customName !== fileItem.file.name) {
            formData.append('file_name', fileItem.customName);
        }
        if (fileItem.documentType) {
            // Send document_type_id (UUID) if available, fallback to document_type (key)
            const dt = documentTypesData?.find(d => d.key === fileItem.documentType);
            if (dt?.document_type_id) {
                formData.append('document_type_id', dt.document_type_id);
            } else {
                formData.append('document_type', fileItem.documentType);
            }
        }

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    setSelectedFiles((prev) =>
                        prev.map((f) => f.id === fileItem.id ? { ...f, progress } : f)
                    );
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const result = JSON.parse(xhr.responseText);
                    resolve(result.document_id);
                } else {
                    reject(new Error(parseErrorResponse(xhr.responseText)));
                }
            });

            xhr.addEventListener('error', () => reject(new Error('Network error')));

            const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || '/api/v1';
            xhr.open('POST', `${baseUrl}${versionPrefix}/documents/upload`);

            const token = storage.getItem('access_token');
            if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }

            xhr.send(formData);
        });
    };

    // Submit handler
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!companyId) {
            setError('Company not found. Please log in again.');
            return;
        }

        if (isEditMode) {
            // EDIT MODE
            if (!containerId) {
                setError('Container ID not found');
                return;
            }

            try {
                // Build the API payload for update
                const payload: ShipmentUpdateData = {
                    status: formData.status.toUpperCase(),
                    physical_status: 'PENDING',

                    // Route
                    origin_port_id: formData.originPortId || undefined,
                    destination_port_id: formData.destinationPortId || undefined,
                    estimated_arrival_date: etaDate ? etaDate.toString() : undefined,
                    carrier_name: formData.carrierName || undefined,

                    // Booking & Vessel
                    booking_number: formData.bookingNumber || undefined,
                    bl_number: formData.blNumber || undefined,
                    container_number: formData.containerNumbers[0]?.trim() || undefined,
                    vessel_name: formData.vesselName || undefined,
                    voyage_number: formData.voyageNumber || undefined,
                    purchase_order_number: formData.purchaseOrderNumber || undefined,

                    // Goods
                    hs_code_primary: formData.hsCodePrimary || undefined,
                    cargo_description: formData.cargoDescription || undefined,
                    total_value_usd: formData.valueAmount ? parseFloat(formData.valueAmount) : undefined,
                    currency: formData.valueCurrency,

                    // Transport
                    transport_mode: formData.transportMode.toUpperCase(),
                    cargo_type: formData.cargoType.toUpperCase(),
                    container_type: formData.containerType,
                    incoterms: formData.incoterms || undefined,

                    // Receiver
                    consignee_company_id: formData.consigneeCompanyId || undefined,
                };

                // Handle quantity based on unit
                if (formData.quantityValue) {
                    const qty = parseFloat(formData.quantityValue);
                    if (!isNaN(qty)) {
                        switch (formData.quantityUnit) {
                            case 'kg':
                                payload.total_weight_kg = qty;
                                break;
                            case 'cbm':
                                payload.total_volume_cbm = qty;
                                break;
                            case 'packages':
                                payload.total_packages = Math.floor(qty);
                                break;
                        }
                    }
                }

                // Handle temperature for reefer
                if (formData.containerType === 'reefer') {
                    payload.requires_temperature_control = true;
                    if (formData.temperatureSetpoint) {
                        payload.temperature_setpoint_celsius = parseFloat(formData.temperatureSetpoint);
                    }

                    const controlledAtmosphere: string[] = [];
                    if (formData.oxygenPercentage) {
                        controlledAtmosphere.push(`O2: ${formData.oxygenPercentage}%`);
                    }
                    if (formData.co2Percentage) {
                        controlledAtmosphere.push(`CO2: ${formData.co2Percentage}%`);
                    }

                    if (controlledAtmosphere.length > 0) {
                        payload.special_instructions = `Controlled Atmosphere: ${controlledAtmosphere.join(', ')}`;
                        payload.extra_metadata = {
                            ...payload.extra_metadata,
                            controlled_atmosphere: {
                                oxygen_percentage: formData.oxygenPercentage || null,
                                co2_percentage: formData.co2Percentage || null,
                            }
                        };
                    }
                }

                // Transshipment ports in extra_metadata
                const selectedPorts = formData.transshipmentPorts.filter(tp => tp.portId);
                if (selectedPorts.length > 0) {
                    payload.extra_metadata = {
                        ...payload.extra_metadata,
                        transshipment_ports: selectedPorts.map(tp => ({
                            port_id: tp.portId,
                            port_name: tp.portDisplay,
                        })),
                    };
                }

                await updateShipment.mutateAsync(payload);

                toast.success(t('form.updateSuccess', 'Container updated successfully'));
                navigate(`/shipper/containers/${containerId}`, { state: navigationStateFrom(location) });

            } catch (err: any) {
                setError(err.message || 'Failed to update container');
                toast.error(t('form.updateError', 'Failed to update container'));
            }
        } else {
            // CREATE MODE
            // Validate booking number is provided (new 2025 model)
            if (!formData.bookingNumber.trim()) {
                setError(t('form.errors.bookingNumberRequired', 'Booking number is required'));
                return;
            }

            // Check if there are documents to upload
            const pendingFiles = selectedFiles.filter(f => f.status === 'pending');
            const hasDocuments = pendingFiles.length > 0;

            // Show progress modal and start at step 1
            setShowCreationProgress(true);
            setCreationProgress({
                step: 'saving',
                totalFiles: hasDocuments ? pendingFiles.length : 0,
            });

            try {
                // Build the base API payload (without container_number)
                const basePayload: Omit<ShipmentCreateData, 'container_number'> = {
                    company_id: companyId,
                    booking_number: formData.bookingNumber.trim(),
                    bl_number: formData.blNumber.trim() || undefined,
                    status: formData.status.toUpperCase(), // API expects uppercase enum
                    physical_status: 'PENDING', // Default physical status

                    // Route
                    origin_port_id: formData.originPortId || undefined,
                    destination_port_id: formData.destinationPortId || undefined,
                    estimated_arrival_date: etaDate ? etaDate.toString() : undefined,
                    carrier_name: formData.carrierName || undefined,

                    // Order
                    purchase_order_number: formData.purchaseOrderNumber || undefined,

                    // Goods
                    hs_code_primary: formData.hsCodePrimary || undefined,
                    cargo_description: formData.cargoDescription || undefined,
                    total_value_usd: formData.valueAmount ? parseFloat(formData.valueAmount) : undefined,
                    currency: formData.valueCurrency,

                    // Transport - API expects uppercase enum values
                    transport_mode: formData.transportMode.toUpperCase(),
                    cargo_type: formData.cargoType.toUpperCase(),
                    container_type: formData.containerType, // container_type uses lowercase in DB
                    incoterms: formData.incoterms || undefined,

                    // Receiver
                    consignee_company_id: formData.consigneeCompanyId || undefined,
                };

                // Handle quantity based on unit
                if (formData.quantityValue) {
                    const qty = parseFloat(formData.quantityValue);
                    if (!isNaN(qty)) {
                        switch (formData.quantityUnit) {
                            case 'kg':
                                basePayload.total_weight_kg = qty;
                                break;
                            case 'cbm':
                                basePayload.total_volume_cbm = qty;
                                break;
                            case 'packages':
                                basePayload.total_packages = Math.floor(qty);
                                break;
                        }
                    }
                }

                // Handle temperature for reefer
                if (formData.containerType === 'reefer') {
                    basePayload.requires_temperature_control = true;
                    if (formData.temperatureSetpoint) {
                        basePayload.temperature_setpoint_celsius = parseFloat(formData.temperatureSetpoint);
                    }

                    // Store controlled atmosphere in extra_metadata and special_instructions
                    const controlledAtmosphere: string[] = [];
                    if (formData.oxygenPercentage) {
                        controlledAtmosphere.push(`O2: ${formData.oxygenPercentage}%`);
                    }
                    if (formData.co2Percentage) {
                        controlledAtmosphere.push(`CO2: ${formData.co2Percentage}%`);
                    }

                    if (controlledAtmosphere.length > 0) {
                        basePayload.special_instructions = `Controlled Atmosphere: ${controlledAtmosphere.join(', ')}`;
                        basePayload.extra_metadata = {
                            ...basePayload.extra_metadata,
                            controlled_atmosphere: {
                                oxygen_percentage: formData.oxygenPercentage || null,
                                co2_percentage: formData.co2Percentage || null,
                            }
                        };
                    }
                }

                // Transshipment ports in extra_metadata
                const selectedTransshipmentPorts = formData.transshipmentPorts.filter(tp => tp.portId);
                if (selectedTransshipmentPorts.length > 0) {
                    basePayload.extra_metadata = {
                        ...basePayload.extra_metadata,
                        transshipment_ports: selectedTransshipmentPorts.map(tp => ({
                            port_id: tp.portId,
                            port_name: tp.portDisplay,
                        })),
                    };
                }

                // Filter out empty container numbers
                const validContainerNumbers = formData.containerNumbers
                    .map(cn => cn.trim())
                    .filter(cn => cn.length > 0);

                // Build payloads: one per container number, or one with no container number
                const payloads: ShipmentCreateData[] = validContainerNumbers.length > 0
                    ? validContainerNumbers.map(cn => ({ ...basePayload, container_number: cn }))
                    : [{ ...basePayload }];

                // Step 1: Save container data (create N containers sequentially under same booking)
                setCreationProgress({ step: 'saving', totalFiles: hasDocuments ? pendingFiles.length : 0 });

                let lastResult: { container_id: string } | undefined;
                for (const payload of payloads) {
                    lastResult = await createShipment.mutateAsync(payload);
                }
                const result = lastResult!;

                // Step 2: Create/Get container folder in Eve Drive
                setCreationProgress({ step: 'creating_folder', totalFiles: hasDocuments ? pendingFiles.length : 0 });
                try {
                    await apiGet<FolderResponse>(`/folders/containers/${result.container_id}`, { requireAuth: true });
                } catch (folderError: any) {
                    // Log error but don't fail container creation
                    console.error('Failed to create container folder:', folderError);
                    // Continue anyway - folder might be created automatically by backend
                }

                // Step 3: Upload documents (if any)
                if (hasDocuments) {
                    setCreationProgress({
                        step: 'uploading_documents',
                        uploadProgress: 0,
                        uploadedFiles: 0,
                        totalFiles: pendingFiles.length,
                    });

                    setIsUploading(true);
                    let uploadedCount = 0;
                    const failedFiles: { name: string; error: string }[] = [];

                    for (const fileItem of pendingFiles) {
                        try {
                            setSelectedFiles(prev =>
                                prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading' as const } : f)
                            );

                            await uploadFileWithProgress(fileItem, result.container_id);

                            uploadedCount++;
                            setCreationProgress({
                                step: 'uploading_documents',
                                uploadProgress: Math.round((uploadedCount / pendingFiles.length) * 100),
                                uploadedFiles: uploadedCount,
                                totalFiles: pendingFiles.length,
                            });

                            setSelectedFiles(prev =>
                                prev.map(f => f.id === fileItem.id ? { ...f, status: 'completed' as const, progress: 100 } : f)
                            );
                        } catch (error: any) {
                            failedFiles.push({ name: fileItem.customName || fileItem.file.name, error: error.message });
                            setSelectedFiles(prev =>
                                prev.map(f => f.id === fileItem.id ? { ...f, status: 'error' as const, error: error.message } : f)
                            );
                            // Continue with other files even if one fails
                        }
                    }
                    setIsUploading(false);

                    // If some files failed, show completed_with_errors
                    if (failedFiles.length > 0) {
                        setCreationProgress({
                            step: 'completed_with_errors',
                            totalFiles: pendingFiles.length,
                            uploadedFiles: uploadedCount,
                            failedFiles,
                            containerId: result.container_id,
                        });

                        // Don't auto-navigate — let the user review and close manually
                        return;
                    }
                }

                // Mark as completed (all succeeded)
                setCreationProgress({ step: 'completed', totalFiles: hasDocuments ? pendingFiles.length : 0 });

                // Wait a moment to show the completion state, then navigate
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Navigate to the new container detail page
                navigate(`/shipper/containers/${result.container_id}`, { state: navigationStateFrom(location) });

            } catch (err: any) {
                // Handle duplicate container number (409 Conflict)
                if (err instanceof ApiClientError && err.status === 409) {
                    let detail: { message?: string; existing_container_id?: string; container_number?: string } = {};
                    try {
                        detail = typeof err.detail === 'string' ? JSON.parse(err.detail) : err.detail;
                    } catch {
                        // detail is not JSON, use raw message
                    }

                    setShowCreationProgress(false);
                    setCreationProgress({ step: 'saving' });

                    if (detail.existing_container_id) {
                        // Same company — container already exists, offer to navigate
                        toast.warning(
                            detail.message || t('form.errors.containerExists', 'This container already exists in your organization.'),
                            {
                                duration: 10000,
                                action: {
                                    label: t('form.errors.viewContainer', 'View container'),
                                    onClick: () => navigate(`/shipper/containers/${detail.existing_container_id}`, { state: navigationStateFrom(location) }),
                                },
                            }
                        );
                    } else {
                        // Different company — cannot access
                        toast.error(
                            detail.message || t('form.errors.containerExistsOtherOrg', 'This container number is already registered by another organization.'),
                        );
                    }
                    return;
                }

                setCreationProgress({
                    step: 'error',
                    error: err.message || 'Failed to create container',
                });
                setError(err.message || 'Failed to create container');
            }
        }
    };

    const isLoading = isEditMode ? isLoadingShipment : false;

    return {
        // State
        formData,
        isEditMode,
        isLoading,
        error,
        etaDate,
        setEtaDate,

        // Search States
        originPortSearch,
        setOriginPortSearch,
        destPortSearch,
        setDestPortSearch,
        hsCodeSearch,
        setHsCodeSearch,
        carrierSearch,
        setCarrierSearch,

        // Handlers
        handleInputChange,
        handlePortSelect,
        handleHsCodeSelect,
        handleSubmit,
        handleAddContainerNumber,
        handleRemoveContainerNumber,
        handleContainerNumberChange,

        // Transshipment ports
        transshipmentPortSearches,
        setTransshipmentPortSearch,
        transshipmentPortOptions,
        handleAddTransshipmentPort,
        handleRemoveTransshipmentPort,
        handleTransshipmentPortSelect,

        // File upload (solo create)
        selectedFiles,
        setSelectedFiles,
        handleFileDrop,
        handleRemoveFile,
        fileInputRef,
        isUploading,

        // Modals
        showAddCarrierModal,
        setShowAddCarrierModal,
        showInviteReceiverModal,
        setShowInviteReceiverModal,
        newCarrierName,
        setNewCarrierName,
        handleAddReceiver,
        handleAddCarrier,

        // Progress (solo create)
        showCreationProgress,
        setShowCreationProgress,
        creationProgress,
        setCreationProgress,

        // Options/Data
        statusOptions,
        cargoTypeOptions,
        transportModeOptions,
        containerTypeOptions,
        incotermsOptions,
        receiverOptions,
        carrierOptions,
        originPortOptions,
        destPortOptions,
        hsCodeOptions,
        orderOptions,
        currencyOptions,
        quantityUnitOptions,

        // Derived
        showTemperatureFields,
        isOrdersEnabled,
        companyId: companyId ?? undefined,
    };
}
